import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Gift, Sparkles, Clock, Zap } from "lucide-react";
import { Button } from "./ui/button";

const PROMO_SHOWN_KEY = "loveconnect_promo_shown";
const PROMO_CONDITIONS_KEY = "loveconnect_promo_conditions";

// Packs promotionnels avec bonus
const PROMO_PACKS = [
  { id: "promo_small", coins: 100, bonus: 25, price: "4,99€", popular: false },
  { id: "promo_medium", coins: 300, bonus: 75, price: "9,99€", popular: true },
  { id: "promo_large", coins: 600, bonus: 180, price: "19,99€", popular: false },
];

export default function PromoPopup({ onClose, onPurchase, language = "fr" }) {
  const [selectedPack, setSelectedPack] = useState("promo_medium");
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePurchase = () => {
    const pack = PROMO_PACKS.find((p) => p.id === selectedPack);
    if (pack) {
      // Mark promo as shown
      localStorage.setItem(PROMO_SHOWN_KEY, "true");
      onPurchase?.(pack);
    }
  };

  const handleClose = () => {
    localStorage.setItem(PROMO_SHOWN_KEY, "true");
    onClose?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-b from-gray-900 to-black border border-yellow-500/30 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with gift animation */}
          <div className="relative bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-yellow-500/20 p-6 text-center">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <motion.div
              animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-3"
            >
              <div className="w-16 h-16 rounded-2xl gradient-gold flex items-center justify-center shadow-lg">
                <Gift className="w-8 h-8 text-black" />
              </div>
            </motion.div>

            <h2 className="text-2xl font-bold text-white mb-1">
              {language === "fr" ? "🎁 Offre Exclusive !" : "🎁 Exclusive Offer!"}
            </h2>
            <p className="text-yellow-400 font-semibold">
              {language === "fr"
                ? "Jusqu'à +30% de pièces BONUS"
                : "Up to +30% BONUS coins"}
            </p>

            {/* Countdown timer */}
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-full">
              <Clock className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-mono font-bold">
                {formatTime(timeLeft)}
              </span>
              <span className="text-red-400/70 text-sm">
                {language === "fr" ? "restant" : "left"}
              </span>
            </div>
          </div>

          {/* Packs */}
          <div className="p-4 space-y-3">
            {PROMO_PACKS.map((pack) => {
              const bonusPercent = Math.round((pack.bonus / pack.coins) * 100);
              const isSelected = selectedPack === pack.id;

              return (
                <button
                  key={pack.id}
                  onClick={() => setSelectedPack(pack.id)}
                  className={`w-full p-4 rounded-2xl border-2 transition-all ${
                    isSelected
                      ? "border-yellow-500 bg-yellow-500/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  } ${pack.popular ? "ring-2 ring-yellow-500/50" : ""}`}
                >
                  {pack.popular && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-yellow-500 text-black text-xs font-bold rounded-full">
                      {language === "fr" ? "POPULAIRE" : "POPULAR"}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-12 h-12 rounded-xl ${
                          isSelected ? "gradient-gold" : "bg-white/10"
                        } flex items-center justify-center`}
                      >
                        <Star
                          className={`w-6 h-6 ${
                            isSelected ? "text-black" : "text-yellow-500"
                          } fill-current`}
                        />
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-lg">
                            {pack.coins}
                          </span>
                          <span className="text-green-400 font-bold text-sm bg-green-500/20 px-2 py-0.5 rounded-full">
                            +{pack.bonus} BONUS
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm">
                          {language === "fr"
                            ? `${pack.coins + pack.bonus} pièces au total`
                            : `${pack.coins + pack.bonus} coins total`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold text-lg">{pack.price}</p>
                      <p className="text-green-400 text-xs font-semibold">
                        +{bonusPercent}% {language === "fr" ? "bonus" : "bonus"}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="p-4 pt-0">
            <Button
              onClick={handlePurchase}
              className="w-full h-14 gradient-gold text-black font-bold text-lg rounded-2xl shadow-lg hover:scale-[1.02] transition-transform"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {language === "fr" ? "Profiter de l'offre" : "Get this offer"}
            </Button>
            <p className="text-center text-muted-foreground text-xs mt-3">
              {language === "fr"
                ? "Offre limitée • Une seule fois par utilisateur"
                : "Limited offer • Once per user"}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to manage promo popup display conditions
export function usePromoPopup() {
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    // Check if promo was already shown
    if (localStorage.getItem(PROMO_SHOWN_KEY) === "true") {
      return;
    }

    // Get or initialize conditions tracking
    const conditions = JSON.parse(
      localStorage.getItem(PROMO_CONDITIONS_KEY) || '{"swipes": 0, "matches": 0, "likes": 0, "startTime": null}'
    );

    if (!conditions.startTime) {
      conditions.startTime = Date.now();
      localStorage.setItem(PROMO_CONDITIONS_KEY, JSON.stringify(conditions));
    }

    // Check conditions every 30 seconds
    const checkConditions = () => {
      const currentConditions = JSON.parse(
        localStorage.getItem(PROMO_CONDITIONS_KEY) || '{"swipes": 0, "matches": 0, "likes": 0, "startTime": null}'
      );

      // Condition 1: 5-15 minutes of usage
      const minutesUsed = (Date.now() - currentConditions.startTime) / 60000;
      if (minutesUsed >= 5) {
        setShowPromo(true);
        return;
      }

      // Condition 2: 10 swipes
      if (currentConditions.swipes >= 10) {
        setShowPromo(true);
        return;
      }

      // Condition 3: 2 matches AND 2 likes received
      if (currentConditions.matches >= 2 && currentConditions.likes >= 2) {
        setShowPromo(true);
        return;
      }
    };

    const interval = setInterval(checkConditions, 30000);
    checkConditions(); // Check immediately

    return () => clearInterval(interval);
  }, []);

  const hidePromo = () => {
    setShowPromo(false);
    localStorage.setItem(PROMO_SHOWN_KEY, "true");
  };

  return { showPromo, hidePromo };
}

// Function to track swipes
export function trackSwipe() {
  const conditions = JSON.parse(
    localStorage.getItem(PROMO_CONDITIONS_KEY) || '{"swipes": 0, "matches": 0, "likes": 0, "startTime": null}'
  );
  conditions.swipes = (conditions.swipes || 0) + 1;
  localStorage.setItem(PROMO_CONDITIONS_KEY, JSON.stringify(conditions));
}

// Function to track matches
export function trackMatch() {
  const conditions = JSON.parse(
    localStorage.getItem(PROMO_CONDITIONS_KEY) || '{"swipes": 0, "matches": 0, "likes": 0, "startTime": null}'
  );
  conditions.matches = (conditions.matches || 0) + 1;
  localStorage.setItem(PROMO_CONDITIONS_KEY, JSON.stringify(conditions));
}

// Function to track received likes
export function trackReceivedLike() {
  const conditions = JSON.parse(
    localStorage.getItem(PROMO_CONDITIONS_KEY) || '{"swipes": 0, "matches": 0, "likes": 0, "startTime": null}'
  );
  conditions.likes = (conditions.likes || 0) + 1;
  localStorage.setItem(PROMO_CONDITIONS_KEY, JSON.stringify(conditions));
}
